import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNonNullableContext } from '@cbridge-utils/context';
import { AlertDialog } from '../AlertDialog/AlertDialog';
import { OpenAlertDialog } from '../AlertDialog/AlertDialog.interface';
import { ConfirmDialog } from '../ConfirmDialog/ConfirmDialog';
import { OpenConfirmDialog } from '../ConfirmDialog/ConfirmDialog.interface';
import { DialogsContext } from '../Provider/DialogsContext';
import { DialogHook } from '../Dialog.interface';

export function useDialogs(): DialogHook {
  const { open, close } = useNonNullableContext(DialogsContext);

  const { t } = useTranslation();

  const alert = useCallback<OpenAlertDialog>(
    (msg, { onClose, ...options } = {}) => open(AlertDialog, { ...options, msg }, { onClose }),
    [open],
  );

  const confirm = useCallback<OpenConfirmDialog>(
    (msg, { onClose, ...options } = {}) => open(ConfirmDialog, { ...options, msg }, { onClose }),
    [open],
  );

  const confirmDelete = useCallback<OpenConfirmDialog>(
    (msg, { onClose, okText = t('text:common.button.delete'), ...options } = {}) => {
      return open(
        ConfirmDialog,
        { ...options, msg, okText, severity: 'error' },
        { onClose },
      );
    },
    [open, t],
  );

  return useMemo(
    () => ({
      alert,
      confirm,
      confirmDelete,
      open,
      close,
    }),
    [alert, close, confirm, confirmDelete, open],
  );
}
